import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LoadingSpinner } from "./LoadingSpinner";
import LabsLoginPage from "../pages/LabsLoginPage"; // Import LabsLoginPage

interface AuthGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireMetarouterUser?: boolean;
}

export default function AuthGuard({
  children,
  requireAdmin = false,
  requireMetarouterUser = false,
}: AuthGuardProps) {
  const { user, isAdmin, isLoading } = useAuth();

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" className="text-blue-600" />
      </div>
    );
  }

  // Special handling for requireMetarouterUser:
  // If user is not logged in OR user email is not @metarouter.io, show LabsLoginPage
  if (requireMetarouterUser) {
    // Check if the user is not authenticated or doesn't have a metarouter.io email
    // Only allow access if the user is fully authenticated with a metarouter.io email
    if (!user || !user.email?.endsWith('@metarouter.io') || user.isAnonymous) {
      return <LabsLoginPage />; // Render LabsLoginPage directly
    }
  }

  // General authentication check (for routes not requiring metarouter user, or if metarouter check passed)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check admin access
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
